import { useDispatch, useSelector, useStore } from "react-redux";
import {
	cancelImportPlayers as cancelImportPlayersAction,
	deleteImportPlayers as deleteImportPlayersAction,
	deletePlayer as deletePlayerAction,
	deletePlayers as deletePlayersAction,
	importPlayers as importPlayersAction,
	parseCsv as parseCsvAction,
	saveImportPlayers as saveImportPlayersAction,
	savePlayer as savePlayerAction
} from "./actions";
import { validateGameData } from "../game-context/actions";

const usePlayersContext = () => {
	const dispatch = useDispatch();
	const store = useStore();

	const usePlayers = (teamId) =>
		useSelector((state) =>
			state.players.players.filter((p) => p.teamId === teamId)
		);

	const useHomePlayers = () =>
		useSelector(({ players, teams }) => {
			return teams.homeTeam
				? players.players.filter((p) => p.teamId === teams.homeTeam.id)
				: [];
		});

	const useAwayPlayers = () =>
		useSelector(({ players, teams }) => {
			return teams.awayTeam
				? players.players.filter((p) => p.teamId === teams.homeTeam.id)
				: [];
		});

	const useAllPlayers = () => useSelector((state) => state.players.players);

	const useImportedPlayers = (teamId) =>
		useSelector((state) => state.players.importedPlayers[teamId] || []);

	const useImportErrors = (teamId) =>
		useSelector((state) => state.players.importErrors[teamId] || []);

	const useIsImporting = () => useSelector((state) => state.players.isImporting);
	const useCsvHeaders = () => useSelector((state) => state.players.csvHeaders);

	const useSavingPlayerIds = () =>
		useSelector((state) => state.players.savingPlayerIds);

	const state = {
		players: usePlayers,
		allPlayers: useAllPlayers,
		homePlayers: useHomePlayers,
		awayPlayers: useAwayPlayers,
		importedPlayers: useImportedPlayers,
		importErrors: useImportErrors,
		isImporting: useIsImporting,
		csvHeaders: useCsvHeaders,
		savingPlayerIds: useSavingPlayerIds
	};

	const validate = () => {
		const { game } = store.getState().game;
		validateGameData(game.id, dispatch);
	};

	const savePlayer = async (player) => {
		const { game } = store.getState().game;

		await savePlayerAction(player, game, dispatch);

		validate();
	};

	const deletePlayer = async (player) => {
		await deletePlayerAction(player, dispatch);
		validate();
	};

	const deletePlayers = async (playerIds) => {
		await deletePlayersAction(playerIds, dispatch);
		validate();
	};

	const importPlayers = async (teamId, results) => {
		importPlayersAction(results, teamId, dispatch);
	};

	const saveImportPlayers = async (teamId, players) => {
		const { game } = store.getState().game;
		const { players: existingPlayers } = store.getState().players;
		await saveImportPlayersAction(
			teamId,
			players,
			existingPlayers.filter((p) => p.teamId === teamId),
			game,
			dispatch
		);
		validate();
	};

	const cancelImportPlayers = () => {
		cancelImportPlayersAction(dispatch);
	};

	const parseCsv = () => {
		parseCsvAction(dispatch);
	};

	const deleteImportPlayers = (teamId, players) => {
		const { importedPlayers } = store.getState().players;
		const currentImportedPlayers = importedPlayers[teamId];
		return deleteImportPlayersAction(
			teamId,
			currentImportedPlayers,
			players,
			dispatch
		);
	};

	return {
		state,
		savePlayer,
		deletePlayer,
		deletePlayers,
		importPlayers,
		saveImportPlayers,
		cancelImportPlayers,
		deleteImportPlayers,
		parseCsv
	};
};

export { usePlayersContext };
